import React, { useState, useRef } from 'react';
import { API_BASE_URL } from '../constants/api';

type Event = {
  id: number;
  event_Name: string;
  start_Datetime: string;
  end_Datetime: string;
  event_Img?: string;
  description: string;
  status: string;
  submitted_By?: string;
  submitted_Email?: string;
  created_At?: string;
};

interface EditEventModalProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedEvent: Event) => void;
}

const EditEventModal: React.FC<EditEventModalProps> = ({ event, isOpen, onClose, onSave }) => {
  const [editEvent, setEditEvent] = useState<Event | null>(event);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to format datetime for datetime-local input
  const formatDateTimeForInput = (dateTimeString: string): string => {
    if (!dateTimeString) return '';

    // Create a Date object from the string
    const date = new Date(dateTimeString);

    // Format as YYYY-MM-DDTHH:MM (local time, not UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Update local state when event prop changes
  React.useEffect(() => {
    setEditEvent(event);
    setImagePreview(event?.event_Img || null);
    setImageFile(null);
  }, [event]);

  if (!isOpen || !editEvent) return null;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file.');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size must be less than 5MB.');
        return;
      }

      setImageFile(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#4f8cff';
    e.currentTarget.style.backgroundColor = '#f8faff';
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#e0e6f0';
    e.currentTarget.style.backgroundColor = '#fff';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#e0e6f0';
    e.currentTarget.style.backgroundColor = '#fff';

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size must be less than 5MB.');
        return;
      }

      setImageFile(file);
      setError('');

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setError('Please drop a valid image file.');
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let imageUrl = editEvent.event_Img;

      // If there's a new image file, upload it first
      if (imageFile) {
        const formData = new FormData();
        // Make sure the field name matches what the API expects
        formData.append('imageFile', imageFile); // This should be 'imageFile', not 'image'

        const uploadResponse = await fetch(`${API_BASE_URL}/events/upload-image`, {
          method: 'POST',
          body: formData
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          console.error('Upload error:', errorData);
          throw new Error('Failed to upload image');
        }

        const uploadResult = await uploadResponse.json();
        imageUrl = uploadResult.data.url;
      }

      // Update the event with the new image URL
      const updatedEvent = {
        ...editEvent,
        event_Img: imageUrl
      };

      const response = await fetch(`${API_BASE_URL}/events/${editEvent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedEvent)
      });

      if (!response.ok) {
        throw new Error('Failed to update event');
      }

      onSave(updatedEvent);
      onClose();
    } catch (err) {
      console.error(err);
      setError('Error updating event.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-modal-backdrop">
      <div className="dashboard-modal" style={{ maxWidth: '600px', width: '90vw' }}>
        <h2>Edit Event</h2>
        {error && <div className="dashboard-modal-error">{error}</div>}
        <form className="dashboard-modal-form" onSubmit={handleSubmit}>
          <label>
            Name
            <input
              type="text"
              value={editEvent.event_Name || ''}
              onChange={e => setEditEvent(ev => ev ? { ...ev, event_Name: e.target.value } : ev)}
              required
            />
          </label>
          <label>
            Start Date and Time
            <input
              type="datetime-local"
              value={formatDateTimeForInput(editEvent.start_Datetime)}
              onChange={e => setEditEvent(ev => ev ? { ...ev, start_Datetime: e.target.value } : ev)}
              required
            />
          </label>
          <label>
            End Date and Time
            <input
              type="datetime-local"
              value={formatDateTimeForInput(editEvent.end_Datetime)}
              onChange={e => setEditEvent(ev => ev ? { ...ev, end_Datetime: e.target.value } : ev)}
              required
            />
          </label>
          <label>
            Status
            <select
              value={editEvent.status || 'Scheduled'}
              onChange={e => setEditEvent(ev => ev ? { ...ev, status: e.target.value } : ev)}
              required
            >
              <option value="Scheduled">Scheduled</option>
              <option value="Completed">Completed</option>
              <option value="Pending">Pending</option>
            </select>
          </label>
          <label>
            Event Image
            <div
              style={{
                border: '2px dashed #e0e6f0',
                borderRadius: '8px',
                padding: '12px',
                textAlign: 'center',
                backgroundColor: '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                margin: '0 8px'
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {imagePreview ? (
                <div style={{ position: 'relative' }}>
                  <img
                    src={imagePreview}
                    alt="Event preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '80px',
                      borderRadius: '4px',
                      border: '1px solid #e0e6f0'
                    }}
                  />
                  {(editEvent.event_Img || imageFile) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage();
                      }}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: '#f36b6b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Remove image"
                    >
                      ×
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '24px', marginBottom: '8px', color: '#666' }}>📷</div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                    Click to upload or drag and drop
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    PNG, JPG, GIF up to 5MB
                  </div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
            </div>
          </label>
          <label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <span>Description</span>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    const textarea = document.querySelector('textarea[name="edit-description"]') as HTMLTextAreaElement;
                    if (textarea) {
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const selectedText = textarea.value.substring(start, end);

                      if (start === end) {
                        // No text selected, insert placeholder bold text
                        const newValue = (editEvent.description || '').substring(0, start) + '<strong>Bold Text</strong>' + (editEvent.description || '').substring(end);
                        setEditEvent(ev => ev ? { ...ev, description: newValue } : ev);
                        setTimeout(() => {
                          textarea.focus();
                          // Position cursor at the end of the bold tag (after </strong>)
                          const boldTagLength = '<strong>Bold Text</strong>'.length;
                          textarea.setSelectionRange(start + boldTagLength, start + boldTagLength);
                        }, 100);
                      } else {
                        // Text selected, wrap it with bold tag
                        const newValue = (editEvent.description || '').substring(0, start) + '<strong>' + selectedText + '</strong>' + (editEvent.description || '').substring(end);
                        setEditEvent(ev => ev ? { ...ev, description: newValue } : ev);
                        setTimeout(() => {
                          textarea.focus();
                          // Position cursor at the end of the bold tag (after </strong>)
                          const boldTagLength = '<strong>'.length + selectedText.length + '</strong>'.length;
                          textarea.setSelectionRange(start + boldTagLength, start + boldTagLength);
                        }, 100);
                      }
                    }
                  }}
                  style={{
                    background: '#ffe6e6',
                    color: '#d32f2f',
                    border: '1px solid #d32f2f',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ffcccc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ffe6e6';
                  }}
                  title="Make selected text bold"
                                  >
                    <strong>B</strong> Bold Text
                  </button>
                <button
                  type="button"
                  onClick={() => {
                    const textarea = document.querySelector('textarea[name="edit-description"]') as HTMLTextAreaElement;
                    if (textarea) {
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const currentValue = editEvent.description || '';
                      const newValue = currentValue.substring(0, start) + '</br>' + currentValue.substring(end);
                      setEditEvent(ev => ev ? { ...ev, description: newValue } : ev);
                      // Set cursor position after the inserted tag
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(start + 5, start + 5);
                      }, 100);
                    }
                  }}
                  style={{
                    background: '#f0f4ff',
                    color: '#3366ff',
                    border: '1px solid #3366ff',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e6f0ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f0f4ff';
                  }}
                  title="+ Add Break to description"
                >
                  + Add Break
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const textarea = document.querySelector('textarea[name="edit-description"]') as HTMLTextAreaElement;
                    if (textarea) {
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const selectedText = textarea.value.substring(start, end);
                      if (start === end) {
                        // No text selected, insert placeholder link
                        const newValue = (editEvent.description || '').substring(0, start) + '<a href="https://example.com">Link Text</a>' + (editEvent.description || '').substring(end);
                        setEditEvent(ev => ev ? { ...ev, description: newValue } : ev);
                        setTimeout(() => {
                          textarea.focus();
                          // Position cursor at the end of the link tag (after </a>)
                          const linkTagLength = '<a href="https://example.com">Link Text</a>'.length;
                          textarea.setSelectionRange(start + linkTagLength, start + linkTagLength);
                        }, 100);
                      } else {
                        // Text selected, wrap it with link tag
                        const newValue = (editEvent.description || '').substring(0, start) + '<a href="https://example.com">' + selectedText + '</a>' + (editEvent.description || '').substring(end);
                        setEditEvent(ev => ev ? { ...ev, description: newValue } : ev);
                        setTimeout(() => {
                          textarea.focus();
                          // Position cursor at the end of the link tag (after </a>)
                          const linkTagLength = '<a href="https://example.com">'.length + selectedText.length + '</a>'.length;
                          textarea.setSelectionRange(start + linkTagLength, start + linkTagLength);
                        }, 100);
                      }
                    }
                  }}
                  style={{
                    background: '#fffbf0',
                    color: '#b8860b',
                    border: '1px solid #b8860b',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fff8e6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fffbf0';
                  }}
                  title="Make selected text a hyperlink"
                >
                  🔗 Make Link
                </button>
              </div>
            </div>
            <textarea
              name="edit-description"
              value={editEvent.description || ''}
              onChange={e => setEditEvent(ev => ev ? { ...ev, description: e.target.value } : ev)}
              style={{ height: '106px', minHeight: '80px', width: '94%', maxWidth: '100%' }}
              placeholder="Enter event description..." 
            />
          </label>
          <div className="dashboard-modal-actions">
            <button
              type="button"
              className="dashboard-modal-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="dashboard-modal-save"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEventModal; 